import { useState } from 'react'
import { ReactSpreadsheetImport } from 'react-spreadsheet-import'
import { useTranslation } from 'react-i18next'
import { ClickAwayListener, Popper } from '@material-ui/core'

import styles from '../DonationsFiltersAndActions.module.scss'

import { Button, Tooltip } from '@percent/lemonade'
import { UploadDonationsImportProps } from './UploadDonationsImport.types'
import { useCurrencies } from '@percent/admin-dashboard/common/hooks'
import { CurrencyInfo } from '@percent/admin-dashboard/api/actions/currencies/currencies.types'
import {
  createDisbursementConfigFields,
  createDonationFields,
  createReassignmentFields,
  createPayrollDeductionFields
} from './UploadDonationsImport.fields'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { usePermissions } from '@percent/admin-dashboard/common/hooks/usePermissions/usePermissions'
import { DonationCategory } from '@percent/admin-dashboard/api/actions/donations/donations.types'

const INT_32_MAX = 2147483647
const hasDecimals = (amount: string) => amount.includes('.')

function stringToBoolean(value: string | undefined) {
  if (value === 'true') return true

  if (value === 'false') return false

  return undefined
}

export const toMinorUnits = (amountInMajor: number, currency: CurrencyInfo) =>
  Math.round(amountInMajor * 10 ** currency.minorUnits)
export const toMajorUnits = (amountInMinor: number, currency: CurrencyInfo) => amountInMinor / 10 ** currency.minorUnits

export function UploadDonationsCsv({
  open,
  setOpen,
  anchorEl,
  handleDonationBatchCreation,
  handleDonationReassignmentBatchCreation,
  handlePayrollDeductionReportsImport,
  isUploadInProgress,
  partnerId,
  canReassign
}: Readonly<UploadDonationsImportProps>) {
  const { t } = useTranslation()
  const { currencyInfo } = useCurrencies()
  const donationImportWithDisbursementConfigFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.AD_DONATION_IMPORT_WITH_DISBURSEMENT_CONFIG
  )
  const reassignDonationFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_REASSIGN_DONATION)
  const internalFgaDoraDonationsFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_DORA_DONATIONS)
  const { userHasPermissions } = usePermissions()

  const canImportDonations = !internalFgaDoraDonationsFeatureFlag || userHasPermissions('donations:can_upload_csv')

  const [importDonationsOpen, setImportDonationsOpen] = useState(false)
  const [importReassignmentsOpen, setImportReassignmentsOpen] = useState(false)
  const [importPayrollDeductionsOpen, setImportPayrollDeductionsOpen] = useState(false)

  const donationFields = createDonationFields(currencyInfo)
  const disbursementConfigFields = createDisbursementConfigFields()
  const reassignmentFields = createReassignmentFields()
  const payrollDeductionFields = createPayrollDeductionFields(currencyInfo)

  const standardFields = [
    ...donationFields,
    ...(donationImportWithDisbursementConfigFeatureFlag ? disbursementConfigFields : [])
  ] as const

  return (
    <>
      <Popper open={open} anchorEl={anchorEl} placement="bottom-end">
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <div className={styles.dropdownContainer}>
            <Button
              size="small"
              variant="secondary"
              onClick={() => setImportDonationsOpen(!importDonationsOpen)}
              disabled={isUploadInProgress || !canImportDonations}
              loading={isUploadInProgress}
            >
              {t('button.importDonations')}
            </Button>
            {reassignDonationFeatureFlag && (
              <Button
                size="small"
                variant="secondary"
                onClick={() => setImportReassignmentsOpen(!importReassignmentsOpen)}
                disabled={isUploadInProgress || !canReassign}
                loading={isUploadInProgress}
              >
                {t('button.importDonationReassignments')}
              </Button>
            )}
            <Tooltip content={!partnerId ? t('tooltip.importAuthorisedPayrollDeductions') : ''}>
              <span>
                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => setImportPayrollDeductionsOpen(!importPayrollDeductionsOpen)}
                  disabled={isUploadInProgress || !partnerId}
                  loading={isUploadInProgress}
                >
                  {t('button.importAuthorisedPayrollDeductions')}
                </Button>
              </span>
            </Tooltip>
          </div>
        </ClickAwayListener>
      </Popper>

      {canImportDonations && (
        <ReactSpreadsheetImport
          isOpen={importDonationsOpen}
          onClose={() => setImportDonationsOpen(false)}
          onSubmit={data => {
            handleDonationBatchCreation(
              data.validData.map(row => ({
                majorAmount: Number(row.majorAmount!),
                currencyCode: row.currencyCode! as string,
                organisationId: row.organisationId! as string,
                subsidiaryId: row.subsidiaryId as string | undefined,
                partnerId: row.partnerId! as string,
                createdAt: row.createdAt as string | undefined,
                disbursementConfigurationType: donationImportWithDisbursementConfigFeatureFlag
                  ? (row.disbursementConfigurationType as 'standalone' | 'bundled' | undefined)
                  : undefined,
                disbursementConfigurationFundSource: donationImportWithDisbursementConfigFeatureFlag
                  ? (row.disbursementConfigurationFundSource as string | undefined)
                  : undefined,
                disbursementConfigurationSendEmail: donationImportWithDisbursementConfigFeatureFlag
                  ? stringToBoolean(row.disbursementConfigurationSendEmail as string | undefined)
                  : undefined,
                disbursementConfigurationReference: donationImportWithDisbursementConfigFeatureFlag
                  ? (row.disbursementConfigurationReference as string | undefined)
                  : undefined,
                category: row.category as DonationCategory | undefined
              }))
            )
          }}
          allowInvalidSubmit={false}
          autoMapSelectValues
          maxRecords={500}
          rowHook={(data, addError) => {
            const amountAsNumber = Number(data.majorAmount)

            if (amountAsNumber <= 0) {
              addError('majorAmount', { message: 'Must be greater than 0', level: 'info' })
            }

            const currency = currencyInfo.find(ci => ci.code === data.currencyCode)

            if (typeof data.majorAmount === 'string' && hasDecimals(data.majorAmount) && currency) {
              const amountDecimalPlaces = data.majorAmount.split('.')[1].length

              if (amountDecimalPlaces > currency.minorUnits) {
                addError('majorAmount', {
                  message: `too many decimal places (${amountDecimalPlaces}) for currency ${currency.code}`,
                  level: 'error'
                })
              }
            }

            if (currency && toMinorUnits(amountAsNumber, currency) > INT_32_MAX) {
              addError('majorAmount', {
                message:
                  `Number is too large, must be less than or equal to: ` +
                  `${toMajorUnits(INT_32_MAX, currency)} for currency: ${currency.code}`,
                level: 'error'
              })
            }

            return data
          }}
          fields={standardFields}
        />
      )}

      {canReassign && (
        <ReactSpreadsheetImport
          isOpen={importReassignmentsOpen}
          onClose={() => setImportReassignmentsOpen(false)}
          onSubmit={data => {
            handleDonationReassignmentBatchCreation(
              data.validData.map(row => ({
                donationId: row.donationId! as string,
                reassignmentReason: row.reason! as string,
                newOrganisationId: row.newOrganisationId! as string,
                oldOrganisationId: row.oldOrganisationId! as string
              }))
            )
          }}
          allowInvalidSubmit={false}
          autoMapSelectValues
          maxRecords={1000}
          fields={reassignmentFields}
        />
      )}
      <ReactSpreadsheetImport
        isOpen={importPayrollDeductionsOpen}
        onClose={() => setImportPayrollDeductionsOpen(false)}
        onSubmit={data => {
          if (partnerId) {
            handlePayrollDeductionReportsImport(
              data.validData.map(row => ({
                donationId: row.donationId! as string,
                currencyCode: row.currencyCode! as string,
                payrollDeductionAmount: row.payrollDeductionAmount! as string
              })),
              partnerId
            )
          }
        }}
        allowInvalidSubmit={false}
        autoMapSelectValues
        maxRecords={1000}
        fields={payrollDeductionFields}
      />
    </>
  )
}
